import React, { useState } from 'react';
import { Alert, Button, Card, Modal, Space, Table, Upload } from 'antd';
import { toast } from '../../../lib/toast';
import { DownloadOutlined, InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { invalidateListResource } from '../../../lib/query/invalidateResource';
import { downloadImportTemplate, importContacts } from '../api/importContacts';
import type { ImportRowError } from '../../../lib/api/types';

const { Dragger } = Upload;

interface ImportContactModalProps {
  open: boolean;
  onClose: () => void;
}

interface DisplayError {
  summary: string;
  rows: ImportRowError[];
}

const ImportContactModal: React.FC<ImportContactModalProps> = ({ open, onClose }) => {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<DisplayError | null>(null);

  const parseError = (err: unknown): DisplayError => {
    const fallback: DisplayError = { summary: 'Import failed. Please try again.', rows: [] };

    const axiosErr = err as { response?: { data?: { detail?: string; errors?: ImportRowError[] } } };
    const data = axiosErr.response?.data;
    if (!data) return fallback;

    return {
      summary: data.detail ?? 'Import failed. Please try again.',
      rows: Array.isArray(data.errors) ? data.errors : [],
    };
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setError(null);

    try {
      const response = await importContacts(file);
      invalidateListResource(queryClient, 'contacts');
      onClose();
      toast.success({
        content: `${response.importedCount} contacts imported successfully. Go to Import History to download or revert.`,
        duration: 6,
      });
    } catch (err: unknown) {
      setError(parseError(err));
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    setDownloading(true);
    try {
      const blob = await downloadImportTemplate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'contact-import-template.xlsx';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const errorColumns = [
    {
      title: 'Row',
      dataIndex: 'code',
      key: 'row',
      width: 80,
      render: (code: string) => {
        const match = code.match(/Row(\d+)/);
        return match ? `#${match[1]}` : code;
      },
    },
    {
      title: 'Error',
      dataIndex: 'description',
      key: 'error',
    },
  ];

  return (
    <Modal
      title="Import Contacts"
      open={open}
      onCancel={importing ? undefined : onClose}
      footer={null}
      width={600}
      destroyOnClose
    >
      {error && (
        <>
          <Alert
            message={error.summary}
            type="error"
            showIcon
            style={{ marginBottom: error.rows.length > 0 ? 0 : 16 }}
            closable
            onClose={() => setError(null)}
          />
          {error.rows.length > 0 && (
            <Table
              dataSource={error.rows}
              columns={errorColumns}
              rowKey={(record) => `${record.code}-${record.description}`}
              size="small"
              pagination={false}
              style={{ marginTop: 12, marginBottom: 16 }}
            />
          )}
        </>
      )}

      <Dragger
        accept=".xlsx,.xls"
        showUploadList={false}
        beforeUpload={(f) => {
          setFile(f);
          setError(null);
          return false;
        }}
        onDrop={(e) => {
          const f = e.dataTransfer.files[0];
          if (f) {
            setFile(f);
            setError(null);
          }
        }}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag an Excel file to this area
        </p>
        <p className="ant-upload-hint">
          Supports .xlsx and .xls files
        </p>
      </Dragger>

      {file && (
        <Alert
          type="info"
          message={`Selected: ${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <Card
        size="small"
        title="Import actions"
        style={{ marginTop: 16 }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
            loading={downloading}
            block
          >
            Download Template
          </Button>
          <Button
            type="primary"
            icon={<UploadOutlined />}
            onClick={handleImport}
            loading={importing}
            disabled={!file}
            block
          >
            {importing ? 'Importing...' : 'Import'}
          </Button>
        </Space>
      </Card>
    </Modal>
  );
};

export default ImportContactModal;
